// src/pages/Profile.js
"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  Avatar,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { toast } from "react-toastify";
import ThemeToggleButton from "../components/ThemeToggleButton";
import PlanBadge from "../components/PlanBadge";
import { getPlans, getMe } from "../api/billing";

const Hero = styled(Box)(({ theme }) => ({
  background:
    theme.palette.mode === "light"
      ? "linear-gradient(180deg, #eef2ff 0%, #ffffff 70%)"
      : "linear-gradient(180deg, #0b1020 0%, #0b0f1a 70%)",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

function stringToHslColor(str = "", s = 65, l = 55) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  return `hsl(${h} ${s}% ${l}%)`;
}

function initialsFromEmail(email) {
  const local = String(email || "?").split("@")[0] || "?";
  const parts = local.replace(/[^a-zA-Z0-9]+/g, " ").trim().split(" ");
  if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/* ---------------- Helpers to render plan limits ---------------- */
const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
function buildPlanFeatures(lim) {
  if (!lim) return ["Plan details unavailable"];

  const f = [];

  // Assistants
  f.push(lim.agents == null ? "Unlimited assistants" : plural(lim.agents, "assistant"));

  // Per-assistant quotas
  if ("urls_per_agent" in lim) {
    f.push(lim.urls_per_agent == null
      ? "Unlimited URLs per assistant"
      : `${plural(lim.urls_per_agent, "URL")} per assistant`);
  }
  if ("files_per_agent" in lim) {
    f.push(lim.files_per_agent == null
      ? "Unlimited files per assistant"
      : `${plural(lim.files_per_agent, "file")} per assistant`);
  }

  // File types + images
  const exts = lim.allowed_exts;
  let typesText;
  if (exts == null) {
    typesText = "All file types";
  } else if (Array.isArray(exts) && exts.length === 1) {
    typesText = `${String(exts[0] || "").toUpperCase()} only`;
  } else if (Array.isArray(exts) && exts.length > 1) {
    typesText = `Allowed: ${exts.map((e) => String(e).toUpperCase()).join(", ")}`;
  } else {
    typesText = "No file types allowed";
  }
  f.push(lim.allow_images === false ? `${typesText} (images not allowed)` : `${typesText} + images`);

  // YouTube ingestion per assistant
  if ("allowed_youtube_urls_per_agent" in lim) {
    f.push(
      lim.allowed_youtube_urls_per_agent == null
        ? "Unlimited YouTube links per assistant"
        : `${plural(lim.allowed_youtube_urls_per_agent, "YouTube link")} per assistant`
    );
  }

  // Chat limits
  const conv = lim.max_conversations_per_month;
  const msgs = lim.max_messages_per_conversation;
  if (conv == null && msgs == null) {
    f.push("Unlimited chat");
  } else {
    const convText = conv == null ? "Unlimited" : conv;
    const msgsText = msgs == null ? "Unlimited" : msgs;
    f.push(`Chat: ${convText} conversations / month · ${msgsText} messages / conversation`);
  }

  return f;
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [signOutOpen, setSignOutOpen] = useState(false);

  // New: billing plan state
  const [me, setMe] = useState(null);           // { plan: "FREE" | ... , ... }
  const [plans, setPlans] = useState([]);       // [{ code, price, currency, limits }]
  const [loadingPlans, setLoadingPlans] = useState(true);

  const avatarColor = useMemo(
    () => stringToHslColor(user?.email || user?.sub || "user"),
    [user]
  );
  const avatarText = useMemo(
    () => (user?.email ? initialsFromEmail(user.email) : "U"),
    [user]
  );

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  // Fetch billing profile + plans
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [p, m] = await Promise.allSettled([getPlans(), getMe(user?.sub)]);
        if (!mounted) return;
        if (p.status === "fulfilled") setPlans(p.value || []);
        if (m.status === "fulfilled") setMe(m.value || null);
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    })();
    return () => { mounted = false; };
  }, [user?.sub]);

  const currentPlanCode = (me?.plan || "FREE").toUpperCase();
  const currentPlan = plans.find((pl) => pl.code === currentPlanCode) || null;
  const planFeatures = useMemo(() => buildPlanFeatures(currentPlan?.limits), [currentPlan]);

  if (!user) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
        <Paper sx={{ p: 3, width: 560, maxWidth: "95vw", borderRadius: 3, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            You’re signed out
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in to view your profile.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Go to Sign in
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}`, backdropFilter: "blur(6px)" }}
      >
        <Toolbar sx={{ minHeight: 60, gap: 1 }}>
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Profile
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <ThemeToggleButton />
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutRoundedIcon />}
            onClick={() => setSignOutOpen(true)}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero / Identity */}
      <Hero>
        <Box sx={{ maxWidth: 1120, mx: "auto", px: 2, py: 4 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems="center">
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: 34,
                  fontWeight: 800,
                  bgcolor: avatarColor,
                  color: "white",
                  boxShadow: 2,
                  border: (t) => `4px solid ${t.palette.background.paper}`,
                }}
              >
                {avatarText}
              </Avatar>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {user.email || "User"}
                </Typography>
                <Chip
                  size="small"
                  icon={user.email_verified ? <VerifiedIcon /> : <ErrorOutlineRoundedIcon />}
                  color={user.email_verified ? "success" : "warning"}
                  label={user.email_verified ? "Email verified" : "Email not verified"}
                  variant={user.email_verified ? "filled" : "outlined"}
                />
                {currentPlanCode && <PlanBadge plan={currentPlanCode} />}
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage your account, security, and plan.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Hero>

      {/* Content */}
      <Box sx={{ maxWidth: 1120, mx: "auto", px: 2, py: 3 }}>
        <Grid container spacing={2.5}>
          {/* Account */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<WorkspacePremiumRoundedIcon />}
                title="Account"
                subheader="Primary contact & sign-in info"
              />
              <Divider />
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="overline" color="text.secondary">Email</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{user.email}</Typography>
                    </Box>
                    <Tooltip title="Copy email">
                      <IconButton onClick={() => copy("Email", user.email)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="overline" color="text.secondary">Status</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" color="success" variant="outlined" label="Active" />
                      {user.email_verified ? (
                        <Chip size="small" color="success" label="Verified" />
                      ) : (
                        <Chip size="small" color="warning" label="Verify your email" />
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Plan & Limits (replaces the old "Identifiers / User ID (sub)" card) */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<WorkspacePremiumRoundedIcon />}
                title="Plan & Limits"
                subheader="Your current plan and usage limits"
                action={
                  loadingPlans ? (
                    <CircularProgress size={20} />
                  ) : currentPlanCode ? (
                    <PlanBadge plan={currentPlanCode} />
                  ) : null
                }
              />
              <Divider />
              <CardContent>
                {loadingPlans ? (
                  <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" color="text.secondary">Loading plan…</Typography>
                  </Stack>
                ) : (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="overline" color="text.secondary">Current plan</Typography>
                      <Chip size="small" label={currentPlanCode || "—"} />
                    </Stack>

                    <List dense sx={{ pt: 0 }}>
                      {planFeatures.map((txt, i) => (
                        <ListItem key={`feat-${i}`} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={txt} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Security */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardHeader
                avatar={<ShieldRoundedIcon />}
                title="Security"
                subheader="Manage sign-out and credentials"
              />
              <Divider />
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Typography variant="body2" color="text.secondary">
                    Signing out ends your session on this device.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<LogoutRoundedIcon />}
                      onClick={() => setSignOutOpen(true)}
                    >
                      Sign out
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Sign-out confirmation */}
      <Dialog open={signOutOpen} onClose={() => setSignOutOpen(false)}>
        <DialogTitle>Sign out?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You’ll be returned to the sign-in page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignOutOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setSignOutOpen(false);
              signOut();
              navigate("/login");
            }}
          >
            Sign out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
